import { Flex } from "antd";
import { useNavigate, useSearchParams } from "react-router-dom";
import apiClient from "QueryClient/axiosClient";
import { useQuery } from "@tanstack/react-query";
import { useUserInfo } from "GlobalStates/User";
import { useEffect } from "react";
import { LandingPageCarouselWithVideo } from "./Carousel/LandingPageCarouselWithVideo";
import { CommunitySection } from "./testimonial/CommunitySection";
import { ROUTES } from "Features/Routes/routeConstants";
import { BeginJourney } from "Features/Pricing/BeginJourney/BeginJourney";
import { Features } from "./Features";
import Footer from "Features/LandingPage/Footer/Footer";
import { useUserLocation } from "GlobalStates/Location";
import { LOCATION_INDIA, LOCATION_USA } from "Constants";
import Scholarship from "Features/PublicScholarship";
import { Finance } from "./Finance.jsx";
import { HeroSection } from "./HeroSection";
import { SEOHead } from "Components/SEO/SEOHead";
import "./style.css";
import ReactGA from "react-ga4";

const fetchTokenUsingRefreshToken = async () => {
  const response = await apiClient.get("/auth/login");
  return response.data;
};

export const LandingPage = () => {
  const navigate = useNavigate();
  const { setUser } = useUserInfo();
  const { setLocation } = useUserLocation();
  const [searchParams] = useSearchParams();
  const baseUrl =
    process.env.REACT_APP_FRONTEND_URL || window.location.origin;
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "OneGrad",
      url: baseUrl,
      logo: `${baseUrl}/favicon-512.png`,
    },
    {
      "@context": "https://schema.org",
      "@type": "Service",
      serviceType: "College admissions guidance",
      provider: {
        "@type": "Organization",
        name: "OneGrad",
      },
      areaServed: "US",
      url: baseUrl,
    },
  ];

  // hit backend with refresh token
  const {
    data: refreshTokenData,
    error,
    isLoading,
  } = useQuery({
    queryFn: fetchTokenUsingRefreshToken,
    queryKey: ["/auth/login"],
  });

  useEffect(() => {
    if (!error && !isLoading && refreshTokenData?.accessToken) {
      ReactGA.event("login", {
        method: "refresh_token",
      });
      setUser({
        token: refreshTokenData?.accessToken,
        tier: refreshTokenData?.tier,
        name: refreshTokenData?.name,
        completedCollegeFinder: refreshTokenData?.completedCollegeFinder,
        picture: refreshTokenData?.picture,
        id: refreshTokenData?.id,
        email: refreshTokenData?.email,
      });
      const searchParamsState = {};
      if (searchParams?.size) {
        searchParams?.forEach((value, key) => {
          searchParamsState[key] = value;
        });
        navigate(searchParams?.get("next"), {
          state: { ...searchParamsState },
        });
      } else {
        navigate(ROUTES.DASHBOARD);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTokenData, error, isLoading, setUser, navigate]);

  useEffect(() => {
    const fetchUserLocation = async () => {
      try {
        const response = await fetch("https://ipapi.co/json/");
        const data = await response.json();
        const country = data.country_name;

        // Smart defaulting logic
        if (country === "India") {
          setLocation(LOCATION_INDIA);
        } else {
          setLocation(LOCATION_USA);
        }
      } catch (error) {
        console.error("Error fetching user location:", error);
        // Fallback location
        setLocation(LOCATION_USA);
      }
    };

    fetchUserLocation();
  }, [setLocation]);

  return (
    <Flex
      className="landing-page-main-container"
      vertical
      align="center"
      justify="center"
      style={{ maxWidth: "100%", overflowX: "hidden" }}
    >
      <SEOHead
        title="OneGrad | AI-Powered College Recommendations"
        description="OneGrad helps students and parents with AI-powered college recommendations, application support, scholarships, and financial planning."
        url="/"
        image="/favicon-512.png"
        type="website"
        jsonLd={jsonLd}
      />
      <HeroSection />
      <LandingPageCarouselWithVideo />
      <Features />
      <CommunitySection />
      <Finance />
      <Scholarship showCardsAndProcess={false} />
      <BeginJourney showHeading={false} />
      <Footer />
    </Flex>
  );
};
